import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../App';
import MasterDashboard from './MasterDashboard';
import ClientAdminDashboard from './ClientAdminDashboard';
import InvestigationDashboard from './InvestigationDashboard';
import ComplaintManagement from './ComplaintManagement';

// Importando ícones
import {
  BarChart3,
  FileText,
  Search,
  Home,
  Database,
  Bot
} from 'lucide-react';

const AdminDashboard = () => {
  // `logout` sai daqui: quem oferece o "Sair" é o MainLayout.
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [authVerified, setAuthVerified] = useState(false);
  
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        if (!user) {
          console.log('❌ Usuário não autenticado, redirecionando...');
          navigate('/admin/login');
          return;
        }

        console.log('✅ Usuário autenticado:', user.username, 'Role:', user.role);
        setAuthVerified(true);
        
        // Definir aba inicial baseada no tipo de usuário
        const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
        if (isClientAdmin) {
          setActiveTab('dashboard');
        } else {
          setActiveTab('master');
        }
      } catch (error) {
        console.error('❌ Erro na verificação de auth:', error);
        toast.error('Erro na verificação de autenticação');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
          <p className="mt-2 text-sm text-gray-500">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  if (!authVerified || !user) {
    return null; // Será redirecionado pelo useEffect
  }

  // Definir abas disponíveis baseado no tipo de usuário
  const getAvailableTabs = () => {
    const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
    if (isClientAdmin) {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: Home, description: 'Visão geral do cliente' },
        { id: 'complaints', label: 'Denúncias', icon: FileText, description: 'Gerenciar denúncias' },
        { id: 'investigation', label: 'Investigação', icon: Search, description: 'Sistema de investigação' }
      ];
    } else {
      // 'users' e 'settings' saíram: vinham desabilitados por regra fixa para
      // todo perfil que as enxergava e só renderizavam um texto dizendo que a
      // funcionalidade não estava disponível. Usuários de verdade ficam em
      // /users, no menu do MainLayout.
      return [
        { id: 'master', label: 'Dashboard Master', icon: BarChart3, description: 'Visão geral do sistema' },
        { id: 'complaints', label: 'Denúncias', icon: FileText, description: 'Gerenciar denúncias' },
        { id: 'investigation', label: 'Investigação', icon: Search, description: 'Sistema de investigação' }
      ];
    }
  };

  const availableTabs = getAvailableTabs();
  const isGlobalAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin;
  const quickLinks = [
    {
      id: 'ia-policies',
      title: 'Políticas IA',
      description: 'Gerenciar base RAG por cliente',
      icon: Database,
      onClick: () => navigate('/admin/policies')
    },
    {
      id: 'ia-agents',
      title: 'Agentes de Investigação',
      description: 'Abrir o painel com investigação e relatórios IA',
      icon: Bot,
      onClick: () => navigate('/investigation')
    }
  ];

  // Renderizar conteúdo da aba ativa
  const renderTabContent = () => {
    switch (activeTab) {
      case 'master':
        return <MasterDashboard />;
      case 'dashboard':
        return <ClientAdminDashboard clientSlug={user.client_slug} clientName={user.client_name} />;
      case 'complaints':
        return <ComplaintManagement />;
      case 'investigation':
        return <InvestigationDashboard />;
      default:
        const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
        return isClientAdmin ? 
          <ClientAdminDashboard clientSlug={user.client_slug} clientName={user.client_name} /> : 
          <MasterDashboard />;
    }
  };

  // Esta tela é renderizada DENTRO do MainLayout, que já entrega a barra
  // superior com o título da página, o menu lateral e o bloco do usuário com o
  // "Sair". O header e a sidebar próprios que existiam aqui duplicavam tudo
  // isso — o resultado era um menu lateral dentro de um ambiente que já tem
  // menu lateral, com "Sair" e nome do usuário repetidos, e "Investigação"/
  // "Usuários" aparecendo nos dois. As abas viram navegação horizontal: continua
  // sendo troca de conteúdo dentro da página, não de rota, então lateral não era
  // o lugar delas.
  return (
    <div className="bg-gray-50">
      <div className="flex flex-col">
        {/* Abas de conteúdo */}
        <nav className="border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-[#149edb] text-[#0d7bb8]'
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
                  }`}
                  title={tab.description}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-[#149edb]' : 'text-gray-400'}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main content */}
        <div className="flex-1">
          {isGlobalAdmin ? (
            <div className="px-6 pt-6">
              <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-slate-900 via-blue-900 to-cyan-800 p-6 text-white shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                      IA Operacional
                    </p>
                    <h2 className="mt-2 text-2xl font-bold">Atalhos visíveis para políticas e agentes</h2>
                    <p className="mt-2 max-w-2xl text-sm text-blue-100">
                      Esses acessos ficam restritos a `super_admin` e `admin`. `client_admin` não vê nem acessa esse fluxo.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  {quickLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={link.id}
                        onClick={link.onClick}
                        className="rounded-xl border border-white/15 bg-white/10 p-4 text-left transition hover:bg-white/15"
                      >
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-white/15 p-2">
                            <Icon className="h-5 w-5 text-cyan-100" />
                          </div>
                          <div>
                            <p className="font-semibold text-white">{link.title}</p>
                            <p className="text-sm text-blue-100">{link.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
